// Parent Dashboard shell: header + persistent section navigation.
// Sections: Home | Children | Progress | Family | Settings
// (/dashboard, /dashboard/children, /dashboard/progress, /dashboard/family,
// /dashboard/settings). All feature panels are reused, just regrouped.
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Crown, LogOut, Settings2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../context/AuthContext";
import { useBrand } from "../context/BrandContext";
import { DashboardNav } from "../components/dashboard/DashboardNav";
import { HomeSection } from "../components/dashboard/sections/HomeSection";
import { ChildrenSection } from "../components/dashboard/sections/ChildrenSection";
import { ProgressSection } from "../components/dashboard/sections/ProgressSection";
import { FamilySection } from "../components/dashboard/sections/FamilySection";
import { SettingsSection } from "../components/dashboard/sections/SettingsSection";
import { api, errMessage } from "../lib/api";

const SECTIONS = ["children", "progress", "family", "settings"];

export default function ParentDashboard() {
  const { user, logout } = useAuth();
  const brand = useBrand();
  const navigate = useNavigate();
  const { section } = useParams();
  const active = SECTIONS.includes(section) ? section : "home";
  const [children, setChildren] = useState(null);
  const [notice, setNotice] = useState(null);
  const [audit, setAudit] = useState([]);
  const [wishlistCount, setWishlistCount] = useState(0);

  const load = useCallback(async () => {
    try {
      const [c, n, a] = await Promise.all([
        api.get("/children"),
        api.get("/consent/notice"),
        api.get("/audit/family"),
      ]);
      setChildren(c.data);
      setNotice(n.data);
      setAudit(a.data.events || []);
    } catch (e) {
      toast.error(errMessage(e));
    }
    // Wishlist badge is a bonus — silently 0 if the kit module is gated off.
    // Counts only kits still waiting to launch (launched ones aren't "waiting").
    api
      .get("/kits-requests")
      .then(({ data }) =>
        setWishlistCount(
          (data.requests || []).filter(
            (r) => r.kit_availability !== "available",
          ).length,
        ),
      )
      .catch(() => setWishlistCount(0));
  }, []);

  useEffect(() => {
    import("../lib/analytics").then(({ track: t }) => t("parent_dashboard_viewed"));
    load();
    // VoiceUsageGuard fires this after quiet-hours changes so child cards sync.
    const refetch = () =>
      api
        .get("/children")
        .then(({ data }) => setChildren(data))
        .catch(() => {});
    window.addEventListener("children-updated", refetch);
    return () => window.removeEventListener("children-updated", refetch);
  }, [load]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-background grain-surface">
      <header className="border-b border-border bg-card/60 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-3">
          <Link
            to="/"
            className="text-xl font-extrabold font-display text-secondary flex items-center gap-2 min-w-0"
            data-testid="dashboard-brand-link"
          >
            {brand.logoSrc && (
              <img
                src={brand.logoSrc}
                alt=""
                className="h-8 w-8 rounded-lg object-contain shrink-0"
                data-testid="dashboard-brand-logo"
              />
            )}
            <span className="truncate">{brand.platform_name}</span>
          </Link>
          <div className="flex items-center gap-2">
            {user?.platform_role !== "platform_admin" &&
              (user?.subscription?.status === "active" ||
              user?.family_premium ? (
                <Badge className="gap-1" data-testid="plan-badge-premium">
                  <Crown className="h-3 w-3" aria-hidden="true" /> Premium
                </Badge>
              ) : (
                <Button
                  asChild
                  size="sm"
                  className="rounded-full gap-2"
                  data-testid="upgrade-button"
                >
                  <Link to="/billing">
                    <Crown className="h-4 w-4" aria-hidden="true" /> Upgrade
                  </Link>
                </Button>
              ))}
            {user?.platform_role === "platform_admin" && (
              <Button
                asChild
                variant="outline"
                size="sm"
                className="nav-btn-dark rounded-full gap-2"
                data-testid="dashboard-admin-link"
              >
                <Link to="/admin">
                  <Settings2 className="h-4 w-4" aria-hidden="true" /> Admin
                </Link>
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="nav-btn-dark rounded-full gap-2"
              onClick={handleLogout}
              data-testid="dashboard-logout-button"
            >
              <LogOut className="h-4 w-4" aria-hidden="true" /> Sign out
            </Button>
            {user?.picture ? (
              <img
                src={user.picture}
                alt={user?.name || user?.email || "Profile"}
                title={user?.email}
                referrerPolicy="no-referrer"
                className="h-9 w-9 rounded-full border-2 border-violet-400 object-cover"
                data-testid="dashboard-profile-photo"
              />
            ) : (
              <span
                className="h-9 w-9 rounded-full border-2 border-violet-400 bg-violet-600 text-white flex items-center justify-center text-sm font-extrabold"
                title={user?.email}
                data-testid="dashboard-profile-initial"
              >
                {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
              </span>
            )}
          </div>
        </div>
      </header>

      <DashboardNav />

      <main className="max-w-6xl mx-auto px-6 py-8">
        {active === "home" && (
          <HomeSection user={user} children={children} wishlistCount={wishlistCount} />
        )}
        {active === "children" && (
          <ChildrenSection children={children} notice={notice} onChanged={load} />
        )}
        {active === "progress" && <ProgressSection />}
        {active === "family" && <FamilySection user={user} />}
        {active === "settings" && <SettingsSection audit={audit} />}
      </main>
    </div>
  );
}
